import React, { useState } from 'react';
import { runFullMigration } from '../../utils/migrateInventory';
import { toast, ToastContainer } from 'react-toastify';
import logger from '../../utils/logger';

const MigrationPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [migrationLog, setMigrationLog] = useState([]);
  const [result, setResult] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvContent(e.target.result);
        addLog(`File loaded: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
        toast.success(`File "${file.name}" loaded successfully`);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
      };
      reader.readAsText(file);
    }
  };

  const addLog = (message) => {
    setMigrationLog(prev => [...prev, { time: new Date().toLocaleTimeString(), message }]);
  };

  const handleMigration = async () => {
    if (!csvContent) {
      toast.error('Please upload a CSV file first');
      return;
    }

    if (!window.confirm('⚠️ WARNING: This will DELETE all existing inventory records and import new data. Are you sure?')) {
      return;
    }

    if (!window.confirm('This action cannot be undone. Please confirm again.')) {
      return;
    }

    setIsProcessing(true);
    setMigrationLog([]);
    setResult(null);

    // Override console.log to capture migration logs
    const originalLog = console.log; // eslint-disable-line no-console
    console.log = (...args) => { // eslint-disable-line no-console
      originalLog(...args);
      addLog(args.join(' '));
    };

    try {
      addLog('🚀 Starting migration process...');
      const migrationResult = await runFullMigration(csvContent);

      // Restore console.log
      console.log = originalLog; // eslint-disable-line no-console

      setResult(migrationResult);

      if (migrationResult.success) {
        addLog('✅ Migration completed successfully!');
        toast.success('Migration completed successfully!');
      } else {
        addLog(`❌ Migration failed: ${migrationResult.error}`);
        toast.error(`Migration failed: ${migrationResult.error}`);
      }
    } catch (error) {
      console.log = originalLog; // eslint-disable-line no-console
      addLog(`❌ Error: ${error.message}`);
      toast.error(`Migration error: ${error.message}`);
      setResult({ success: false, error: error.message });
    }

    setIsProcessing(false);
  };

  const clearLogs = () => {
    setMigrationLog([]);
    setResult(null);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <ToastContainer />

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold mb-2">Inventory Migration Tool</h1>
        <p className="text-gray-600 mb-6">
          Migrate from old inventory system to new Stock & Movement structure
        </p>

        {/* Warning Notice */}
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Danger Zone</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>This migration will:</p>
                <ul className="list-disc list-inside mt-1">
                  <li>DELETE all existing inventory records permanently</li>
                  <li>Import new stock data from CSV file</li>
                  <li>Create opening stock movements</li>
                  <li>This action CANNOT be undone</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Stock CSV File
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={isProcessing}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
          </div>
          {csvContent && (
            <p className="mt-2 text-sm text-green-600">
              ✓ CSV file loaded ({csvContent.split('\n').length - 1} rows)
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={handleMigration}
            disabled={!csvContent || isProcessing}
            className={`px-6 py-3 rounded-md text-white font-semibold ${
              !csvContent || isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isProcessing ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Run Migration'
            )}
          </button>

          {migrationLog.length > 0 && (
            <button
              onClick={clearLogs}
              disabled={isProcessing}
              className="px-6 py-3 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 disabled:opacity-50"
            >
              Clear Logs
            </button>
          )}
        </div>

        {/* Migration Result Summary */}
        {result && (
          <div className={`p-4 rounded-md mb-6 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <h3 className={`font-bold text-lg mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? '✅ Migration Successful' : '❌ Migration Failed'}
            </h3>
            {result.success && result.summary && (
              <div className="text-sm text-green-700">
                <p><strong>Deleted:</strong> {result.summary.deleted} old inventory records</p>
                <p><strong>Imported:</strong> {result.summary.imported} new stock items</p>
                <p><strong>Movements Created:</strong> {result.summary.movementsCreated} opening stock records</p>
              </div>
            )}
            {!result.success && (
              <p className="text-sm text-red-700">{result.error}</p>
            )}
          </div>
        )}

        {/* Migration Log */}
        {migrationLog.length > 0 && (
          <div className="bg-gray-900 text-green-400 p-4 rounded-md font-mono text-sm max-h-96 overflow-y-auto">
            <h3 className="text-white font-bold mb-2">Migration Log:</h3>
            {migrationLog.map((log, index) => (
              <div key={index} className="mb-1">
                <span className="text-gray-500">[{log.time}]</span> {log.message}
              </div>
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="font-bold text-blue-900 mb-2">Instructions</h3>
          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
            <li>Ensure you have a backup of your current inventory data</li>
            <li>Upload the stock CSV file (stock..csv or stock_inventory.csv)</li>
            <li>Review the file preview to ensure data is correct</li>
            <li>Click "Run Migration" button</li>
            <li>Confirm the two warning dialogs</li>
            <li>Wait for the migration to complete</li>
            <li>Review the migration log for any errors</li>
            <li>Navigate to Inventory page to verify imported stock</li>
          </ol>
        </div>

        {/* CSV Format Guide */}
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-4">
          <h3 className="font-bold text-gray-900 mb-2">Expected CSV Format</h3>
          <p className="text-sm text-gray-700 mb-2">The CSV file should have the following columns:</p>
          <div className="bg-white p-2 rounded border border-gray-300 text-xs font-mono">
            S/n, ITEM, PART NUMBER, QUANTITY, LOCATION, STOCK VALUE, TOTAL
          </div>
          <p className="text-sm text-gray-600 mt-2">
            <strong>Example:</strong> 1,OIL FILTER (TOYOTA),90915-YZZE1,48,AY-A-1A2,"4,000","192,000"
          </p>
        </div>
      </div>
    </div>
  );
};

export default MigrationPage;
